import React, { useMemo } from "react";
import { Button, Drawer, Table, Tag, Typography } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { statusTone } from "../../shared/statusColors";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  InternalTaskGroup,
  InternalTaskRow,
  fmtINR,
  fmtQty,
  num,
} from "./internalTaskShared";

// Drill-down for the KPI card's "N unassigned" caption: every loaded task
// with a blank internal task number, flattened out of the (per-UOM) Unassigned
// groups. Data is already embedded in the summary payload — no fetch.

const INK = "#022c57";

const UnassignedTasksDrawer: React.FC<{
  open: boolean;
  groups: InternalTaskGroup[];
  onClose: () => void;
  getContainer?: () => HTMLElement;
  onExport?: () => void;
  exporting?: boolean;
}> = ({ open, groups, onClose, getContainer, onExport, exporting }) => {
  const wbsName = useWbsName();
  const tasks = useMemo(
    () =>
      groups
        .filter((group) => group.isUnassigned)
        .flatMap((group) => group.tasks || []),
    [groups]
  );

  const totalPlanned = useMemo(
    () => tasks.reduce((sum, task) => sum + num(task.plannedAmount), 0),
    [tasks]
  );

  const columns: ColumnsType<InternalTaskRow> = [
    {
      title: "Task",
      dataIndex: "taskName",
      width: 260,
      render: (_: any, task) => (
        <div>
          <span
            style={{ fontFamily: "monospace", fontSize: 12, color: INK }}
          >
            {task.taskSequenceId || "—"}
          </span>
          <div style={{ fontSize: 12.5 }}>{task.taskName || "—"}</div>
        </div>
      ),
    },
    {
      title: "Phase",
      dataIndex: "phaseName",
      width: 140,
      ellipsis: true,
      render: (value: string) => wbsName(value) || "—",
    },
    {
      title: "Activity",
      dataIndex: "activityName",
      width: 160,
      ellipsis: true,
      render: (value: string) => wbsName(value) || "—",
    },
    {
      title: "Status",
      dataIndex: "taskStatus",
      width: 110,
      render: (value: string) => {
        const tone = statusTone(value);
        return (
          <Tag
            style={{
              marginRight: 0,
              fontSize: 11,
              color: tone.text,
              background: tone.bg,
              borderColor: "transparent",
            }}
          >
            {value || "—"}
          </Tag>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "workQuantity",
      width: 120,
      align: "right",
      render: (value: string, task) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtQty(num(value))}
          {task.uomName ? (
            <span style={{ color: "#8c8c8c", fontSize: 11 }}>
              {" "}
              {task.uomName}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      title: "Planned Rate (Basic)",
      dataIndex: "rate",
      width: 120,
      align: "right",
      render: (value: string) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtINR(num(value))}
        </span>
      ),
    },
    {
      title: "Planned Amount",
      dataIndex: "plannedAmount",
      width: 140,
      align: "right",
      render: (value: string) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: INK }}>
          {fmtINR(num(value))}
        </span>
      ),
    },
  ];

  return (
    <Drawer
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          Unassigned Tasks
          <Tag color="orange" style={{ marginRight: 0 }}>
            {tasks.length}
          </Tag>
        </span>
      }
      placement="right"
      width="min(980px, 100vw)"
      onClose={onClose}
      open={open}
      destroyOnClose
      {...(getContainer ? { getContainer } : {})}
      extra={
        onExport ? (
          <Button
            icon={<FileExcelOutlined />}
            loading={exporting}
            disabled={tasks.length === 0}
            onClick={onExport}
          >
            Export
          </Button>
        ) : null
      }
      footer={
        tasks.length > 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
              color: INK,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span>Planned Amount Total</span>
            <span>{fmtINR(totalPlanned)}</span>
          </div>
        ) : null
      }
    >
      <Typography.Paragraph
        type="secondary"
        style={{ fontSize: 12.5, marginBottom: 12 }}
      >
        These tasks have no Internal Task Number, so they are excluded from the
        numbered groups above. Assign a number via the task edit form to move
        them into their group.
      </Typography.Paragraph>
      <Table<InternalTaskRow>
        size="small"
        rowKey={(task) => task.taskId ?? task.taskSequenceId ?? ""}
        columns={columns}
        dataSource={tasks}
        pagination={
          tasks.length > 25
            ? { defaultPageSize: 25, showSizeChanger: true, size: "small" }
            : false
        }
        scroll={{ x: 1000 }}
      />
    </Drawer>
  );
};

export default UnassignedTasksDrawer;
